import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import './App.css'

type Duration = 5 | 10 | 20

function App() {
  const [selectedDuration, setSelectedDuration] = useState<Duration>(5)
  const [isActive, setIsActive] = useState(false)
  const [timeLeft, setTimeLeft] = useState(5 * 60)
  const [isComplete, setIsComplete] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const totalSeconds = selectedDuration * 60
  const progress = isActive ? (totalSeconds - timeLeft) / totalSeconds : 0

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsActive(false)
            setIsComplete(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isActive, timeLeft])

  const handleDurationSelect = (duration: Duration) => {
    if (!isActive) {
      setSelectedDuration(duration)
      setTimeLeft(duration * 60)
      setIsComplete(false)
    }
  }

  const handleToggle = () => {
    if (isComplete) {
      setTimeLeft(selectedDuration * 60)
      setIsComplete(false)
    }
    setIsActive(!isActive)
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Responsive circle sizes
  const mobileRadius = 100
  const desktopRadius = 140
  const mobileCircumference = 2 * Math.PI * mobileRadius
  const desktopCircumference = 2 * Math.PI * desktopRadius

  // Generate stars
  const stars = Array.from({ length: 80 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2 + 0.5,
    duration: Math.random() * 3 + 2,
    delay: Math.random() * 5
  }))

  return (
    <div className="h-full h-dvh w-full flex items-center justify-center overflow-hidden relative">
      {/* Darker Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950" />

      {/* Stars */}
      <div className="absolute inset-0 overflow-hidden">
        {stars.map((star) => (
          <motion.div
            key={star.id}
            className="absolute rounded-full bg-white"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
            }}
            animate={{
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.2, 1]
            }}
            transition={{
              duration: star.duration,
              repeat: Infinity,
              delay: star.delay,
              ease: 'easeInOut'
            }}
          />
        ))}
      </div>

      {/* Ambient Glow */}
      <motion.div
        className="absolute top-1/2 left-1/2 w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] md:w-[600px] md:h-[600px] rounded-full blur-3xl opacity-30"
        style={{
          background: 'radial-gradient(circle, rgb(99, 102, 241) 0%, transparent 70%)',
          x: '-50%',
          y: '-50%'
        }}
        animate={{
          scale: isActive ? [1, 1.2, 1] : 1,
        }}
        transition={{
          duration: 4,
          repeat: isActive ? Infinity : 0,
          ease: 'easeInOut'
        }}
      />

      <div className="relative z-10 flex flex-col items-center gap-8 sm:gap-12 md:gap-16 compact-layout px-4 py-8 sm:px-6 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-white/90 tracking-wider mb-1 sm:mb-2">Zen</h1>
          <p className="text-indigo-300/60 text-xs sm:text-sm font-light tracking-widest uppercase">Meditation Timer</p>
        </motion.div>

        {/* Timer Container */}
        <div className="relative w-[280px] h-[280px] sm:w-[340px] sm:h-[340px] md:w-[380px] md:h-[380px] flex items-center justify-center flex-shrink-0">
          <div className="timer-aura absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.div
              className="absolute rounded-full timer-aura-core"
              animate={{
                scale: isActive ? [0.96, 1.08, 0.98] : [0.98, 1.02, 0.99],
                opacity: isActive ? [0.42, 0.7, 0.48] : [0.3, 0.4, 0.32]
              }}
              transition={{
                duration: isActive ? 6 : 10,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
            <motion.div
              className="absolute rounded-full timer-aura-wave"
              animate={{
                scale: isActive ? [0.9, 1.18, 0.94] : [0.94, 1.06, 0.96],
                opacity: isActive ? [0.18, 0.34, 0.2] : [0.12, 0.18, 0.14]
              }}
              transition={{
                duration: isActive ? 7.5 : 11,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
          </div>

          {/* Breathing Guide Circle */}
          <AnimatePresence>
            {isActive && (
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full bg-indigo-400/10 border border-indigo-400/20"
                  animate={{
                    scale: [1, 1.8, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Progress Ring Background - Mobile */}
          <svg className="absolute inset-0 w-full h-full -rotate-90 sm:hidden">
            <circle
              cx="140"
              cy="140"
              r={mobileRadius}
              stroke="rgb(255 255 255 / 0.05)"
              strokeWidth="2.5"
              fill="none"
            />
          </svg>

          {/* Progress Ring Background - Desktop */}
          <svg className="absolute inset-0 w-full h-full -rotate-90 hidden sm:block">
            <circle
              cx="190"
              cy="190"
              r={desktopRadius}
              stroke="rgb(255 255 255 / 0.05)"
              strokeWidth="3"
              fill="none"
            />
          </svg>

          {/* Animated Progress Ring - Mobile */}
          <svg className="absolute inset-0 w-full h-full -rotate-90 sm:hidden">
            <defs>
              <linearGradient id="progressGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(165, 180, 252)" />
                <stop offset="50%" stopColor="rgb(129, 140, 248)" />
                <stop offset="100%" stopColor="rgb(99, 102, 241)" />
              </linearGradient>
              <filter id="glowMobile">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <motion.circle
              cx="140"
              cy="140"
              r={mobileRadius}
              stroke="url(#progressGradientMobile)"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              filter="url(#glowMobile)"
              initial={{ strokeDasharray: mobileCircumference, strokeDashoffset: mobileCircumference }}
              animate={{
                strokeDashoffset: mobileCircumference - (progress * mobileCircumference),
                opacity: isActive ? 1 : 0.5
              }}
              transition={{
                strokeDashoffset: { duration: 1, ease: 'easeInOut' },
                opacity: { duration: 0.3 }
              }}
            />
          </svg>

          {/* Animated Progress Ring - Desktop */}
          <svg className="absolute inset-0 w-full h-full -rotate-90 hidden sm:block">
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(165, 180, 252)" />
                <stop offset="50%" stopColor="rgb(129, 140, 248)" />
                <stop offset="100%" stopColor="rgb(99, 102, 241)" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <motion.circle
              cx="190"
              cy="190"
              r={desktopRadius}
              stroke="url(#progressGradient)"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              filter="url(#glow)"
              initial={{ strokeDasharray: desktopCircumference, strokeDashoffset: desktopCircumference }}
              animate={{
                strokeDashoffset: desktopCircumference - (progress * desktopCircumference),
                opacity: isActive ? 1 : 0.5
              }}
              transition={{
                strokeDashoffset: { duration: 1, ease: 'easeInOut' },
                opacity: { duration: 0.3 }
              }}
            />
          </svg>

          {/* Center Content */}
          <div className="relative z-10 text-center">
            <AnimatePresence mode="wait">
              {isComplete ? (
                <motion.div
                  key="complete"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex flex-col items-center gap-3 sm:gap-4"
                >
                  <div className="text-5xl sm:text-6xl">🙏</div>
                  <div className="text-xl sm:text-2xl font-light text-white/90 tracking-wide">Complete</div>
                  <div className="text-xs sm:text-sm text-indigo-300/60 tracking-wider">Well done</div>
                </motion.div>
              ) : (
                <motion.div
                  key="timer"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex flex-col items-center gap-2 sm:gap-3"
                >
                  <motion.div
                    className="text-5xl sm:text-6xl md:text-7xl font-light text-white/95 tabular-nums tracking-tight"
                    animate={{
                      scale: isActive && timeLeft % 60 === 0 && timeLeft !== totalSeconds ? [1, 1.05, 1] : 1
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {formatTime(timeLeft)}
                  </motion.div>
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs sm:text-sm text-indigo-300/60 tracking-widest uppercase"
                    >
                      Breathe
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col items-center gap-6 sm:gap-8">
          {/* Play/Pause Button */}
          <motion.button
            onClick={handleToggle}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/90 hover:bg-white/10 hover:border-white/20 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isComplete ? (
              <svg className="w-7 h-7 sm:w-8 sm:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v16l16-8L4 4z" transform="translate(2, 0)" />
              </svg>
            ) : isActive ? (
              <svg className="w-7 h-7 sm:w-8 sm:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 9v6m4-6v6" />
              </svg>
            ) : (
              <svg className="w-7 h-7 sm:w-8 sm:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3l14 9-14 9V3z" />
              </svg>
            )}
          </motion.button>

          {/* Duration Presets */}
          <div className="flex gap-2 sm:gap-3 md:gap-4">
            {([5, 10, 20] as const).map((duration) => (
              <motion.button
                key={duration}
                onClick={() => handleDurationSelect(duration)}
                disabled={isActive}
                className={`px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base rounded-full font-light tracking-wider transition-all ${
                  selectedDuration === duration
                    ? 'bg-indigo-500/30 border border-indigo-400/50 text-white shadow-lg shadow-indigo-500/20'
                    : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-white/80'
                } ${isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                whileHover={!isActive ? { scale: 1.05 } : {}}
                whileTap={!isActive ? { scale: 0.95 } : {}}
              >
                {duration} min
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default App
